import { useEffect, useRef, useState } from 'react';

type TypewriterTextOptions = {
    readonly animate: boolean;
    readonly animationKey: string;
    readonly charsPerSecond?: number;
    readonly onRevealFrame?: () => void;
};

const DEFAULT_CHARS_PER_SECOND = 180;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function shouldSnapToFullText(): boolean {
    if (
        typeof document === 'undefined' ||
        typeof window === 'undefined' ||
        typeof window.matchMedia !== 'function'
    ) {
        return true;
    }

    return (
        document.visibilityState !== 'visible' ||
        window.matchMedia(REDUCED_MOTION_QUERY).matches
    );
}

export function useTypewriterText(
    text: string,
    {
        animate,
        animationKey,
        charsPerSecond = DEFAULT_CHARS_PER_SECOND,
        onRevealFrame,
    }: TypewriterTextOptions,
): string {
    const onRevealFrameRef = useRef(onRevealFrame);
    const [renderedText, setRenderedText] = useState(() =>
        animate && text.length > 0 && !shouldSnapToFullText() ? '' : text,
    );

    useEffect(() => {
        onRevealFrameRef.current = onRevealFrame;
    }, [onRevealFrame]);

    useEffect(() => {
        if (!animate || text.length === 0 || shouldSnapToFullText()) {
            setRenderedText(text);
            return;
        }

        let animationFrameId: number | null = null;
        let startTimestampMs: number | null = null;
        let latestRenderedText = '';
        const safeCharsPerSecond = Math.max(1, charsPerSecond);

        function cancelAnimationFrameIfScheduled() {
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
                animationFrameId = null;
            }
        }

        function snapToFullText() {
            cancelAnimationFrameIfScheduled();
            if (latestRenderedText !== text) {
                latestRenderedText = text;
                setRenderedText(text);
                onRevealFrameRef.current?.();
            }
        }

        function handleMotionPreferenceChange() {
            if (shouldSnapToFullText()) {
                snapToFullText();
            }
        }

        function revealFrame(timestampMs: number) {
            if (shouldSnapToFullText()) {
                snapToFullText();
                return;
            }

            startTimestampMs ??= timestampMs;
            const elapsedMs = timestampMs - startTimestampMs;
            const nextLength = Math.min(
                text.length,
                Math.max(
                    1,
                    Math.floor((elapsedMs * safeCharsPerSecond) / 1000),
                ),
            );
            const nextRenderedText = text.slice(0, nextLength);
            if (nextRenderedText !== latestRenderedText) {
                latestRenderedText = nextRenderedText;
                setRenderedText(nextRenderedText);
                onRevealFrameRef.current?.();
            }

            if (nextLength < text.length) {
                animationFrameId = window.requestAnimationFrame(revealFrame);
            }
        }

        latestRenderedText = '';
        setRenderedText('');
        animationFrameId = window.requestAnimationFrame(revealFrame);

        const mediaQueryList = window.matchMedia(REDUCED_MOTION_QUERY);
        document.addEventListener(
            'visibilitychange',
            handleMotionPreferenceChange,
        );
        mediaQueryList.addEventListener('change', handleMotionPreferenceChange);

        return () => {
            cancelAnimationFrameIfScheduled();
            document.removeEventListener(
                'visibilitychange',
                handleMotionPreferenceChange,
            );
            mediaQueryList.removeEventListener(
                'change',
                handleMotionPreferenceChange,
            );
        };
    }, [animate, animationKey, charsPerSecond, text]);

    return renderedText;
}
